import { useState } from 'react';
import { createHotelRate, updateHotelRate, deleteHotelRate, parseOccupancy, money } from '../../data/hotels';

const blank = (rooms = [], boards = []) => ({
  roomCategory: rooms[0]?.key || '',
  roomTypeCode: '',
  mealTypeCode: boards[0] || '',
  adults: 2,
  bands: [],
  price: '',
  minimumStay: 0,
  maximumStay: 0,
  beginDate: '',
  endDate: '',
  bookingBegin: '',
  bookingEnd: '',
  note: '',
});

const fromRate = (r) => {
  const { adults, bands } = parseOccupancy(r.accommodationCode);
  return {
    roomCategory: r.roomCategoryCode || '',
    roomTypeCode: r.roomTypeCode || '',
    mealTypeCode: r.mealTypeCode || '',
    adults,
    bands,
    price: r.sellPrice ?? '',
    minimumStay: r.minimumStay || 0,
    maximumStay: r.maximumStay || 0,
    beginDate: r.beginDate || '',
    endDate: r.endDate || '',
    bookingBegin: r.bookingBegin || '',
    bookingEnd: r.bookingEnd || '',
    note: r.note || '',
  };
};

const label = { fontSize:11.5, fontWeight:800, color:'#5a726f', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 };

/** "Junior Suite King (SKNG)" -> "Junior Suite King" — the part a visitor actually sees. */
const displayOf = (key) => String(key).replace(/\s*\([^()]*\)\s*$/, '').trim();

/**
 * What the typed room name will do when saved. The trap this exists for: the supplier's room
 * carries a code, "Junior Suite King (SKNG)". Typing the name without it does not price over that
 * room — it makes a second one, and the site shows the code-stripped name for both, so a visitor
 * sees the same room twice at two prices.
 */
const roomIntent = (typed, rooms) => {
  const name = typed.trim();
  if (!name) return null;

  const lower = name.toLowerCase();
  const exact = rooms.find(r => r.key.trim().toLowerCase() === lower);
  if (exact) return { kind:'override', room: exact };

  const sameName = rooms.find(r => displayOf(r.key).toLowerCase() === lower);
  if (sameName) return { kind:'clash', room: sameName };

  return { kind:'new' };
};

/**
 * A price we set ourselves: one room, one stay window, one party. The same five dimensions the
 * supplier prices on, because the row lands in the same table and is read by the same code.
 */
export default function HotelRateForm({ hotel, rate, rooms, boards, onClose, onSaved }) {
  const editing = !!rate;
  const [v, setV] = useState(editing ? fromRate(rate) : blank(rooms, boards));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const set = (k, val) => setV(prev => ({ ...prev, [k]: val }));
  const intent = roomIntent(v.roomCategory, rooms);

  const setBand = (i, patch) => setV(prev => ({
    ...prev, bands: prev.bands.map((b, j) => j === i ? { ...b, ...patch } : b),
  }));
  const addBand = () => setV(prev => ({ ...prev, bands: [...prev.bands, { count:1, minAge:3, maxAge:12 }] }));
  const dropBand = (i) => setV(prev => ({ ...prev, bands: prev.bands.filter((_, j) => j !== i) }));

  const body = () => ({
    roomCategory: v.roomCategory.trim(),
    roomTypeCode: v.roomTypeCode.trim() || null,
    mealTypeCode: v.mealTypeCode.trim() || null,
    adults: Number(v.adults),
    children: v.bands.map(b => ({ count: Number(b.count), minAge: Number(b.minAge), maxAge: Number(b.maxAge) })),
    price: Number(v.price),
    currencyCode: 'USD',
    perRoom: true,
    minimumStay: Number(v.minimumStay) || 0,
    maximumStay: Number(v.maximumStay) || 0,
    beginDate: v.beginDate,
    endDate: v.endDate,
    bookingBegin: v.bookingBegin || null,
    bookingEnd: v.bookingEnd || null,
    note: v.note.trim() || null,
  });

  const validate = () => {
    if (!v.roomCategory.trim()) return 'Which room is this price for?';
    if (!v.beginDate || !v.endDate) return 'Give the stay window a start and an end.';
    if (v.endDate < v.beginDate) return 'The stay window ends before it begins.';
    if (v.price === '' || Number(v.price) < 0) return 'Give it a price.';
    for (const b of v.bands) {
      if (Number(b.minAge) > Number(b.maxAge)) return 'A child band cannot end before it starts.';
    }
    return null;
  };

  /** `again` keeps the form open with the same values — the next season is usually one date change away. */
  const save = async (again = false) => {
    if (saving) return;
    const bad = validate();
    if (bad) { setError(bad); return; }

    setSaving(true); setError(null); setSaved(null);
    try {
      if (editing) await updateHotelRate(hotel.id, rate.id, body());
      else await createHotelRate(hotel.id, body());
      await onSaved();
      if (again) setSaved(`Saved ${money(Number(v.price))} for ${v.beginDate} – ${v.endDate}. Change the dates for the next season.`);
      else onClose();
    } catch (e) { setError(e.message || 'Could not save that price.'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (saving || !window.confirm('Delete this price? The supplier price for that room takes over again.')) return;
    setSaving(true); setError(null);
    try { await deleteHotelRate(hotel.id, rate.id); await onSaved(); onClose(); }
    catch (e) { setError(e.message || 'Could not delete that price.'); setSaving(false); }
  };

  return (
    <div onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position:'fixed', inset:0, zIndex:140, display:'flex', justifyContent:'flex-end', background:'rgba(6,40,45,0.4)', backdropFilter:'blur(5px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:460, height:'100%', overflowY:'auto', padding:26, background:'rgba(255,255,255,0.96)', backdropFilter:'blur(28px) saturate(180%)', boxShadow:'-24px 0 60px rgba(6,40,45,0.28)' }}>

        <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:5 }}>
          <div className="serif" style={{ fontSize:22, flex:1, lineHeight:1.15 }}>{editing ? 'Edit your price' : 'Add a price'}</div>
          <span onClick={onClose} style={{ cursor:'pointer', fontSize:22, color:'#5a726f', lineHeight:1 }}>×</span>
        </div>
        <div style={{ fontSize:12.5, color:'#5a726f', marginBottom:18 }}>
          {hotel.displayName || hotel.name} · this is the price customers pay, with no margin added.
          It replaces the supplier's prices for this room.
        </div>

        <div style={{ display:'grid', gap:14 }}>
          <div>
            <div style={label}>Room</div>
            <input list="hc-rooms" value={v.roomCategory} onChange={e => set('roomCategory', e.target.value)} className="a-input" placeholder="Ocean View Junior Suite" />
            <datalist id="hc-rooms">{rooms.map(r => <option key={r.key} value={r.key} />)}</datalist>

            {!intent && (
              <div style={{ fontSize:12, color:'#5a726f', marginTop:5 }}>
                Start typing to reuse one of this hotel's rooms, or name a new one.
              </div>
            )}
            {intent?.kind === 'override' && (
              <div style={{ fontSize:12, color:'#1a7a48', fontWeight:600, marginTop:5 }}>
                Prices over <b>{intent.room.label}</b> — yours becomes the only price for that room.
              </div>
            )}
            {intent?.kind === 'new' && (
              <div style={{ fontSize:12, color:'#5a726f', marginTop:5 }}>
                A new room. It joins the others on the hotel page once it has a price.
              </div>
            )}
            {intent?.kind === 'clash' && (
              <div style={{ fontSize:12, marginTop:6, padding:'9px 11px', borderRadius:10, background:'rgba(169,118,26,0.12)', color:'#8a6114', lineHeight:1.5 }}>
                The supplier calls this room <b>{intent.room.key}</b>. Saved as typed it becomes a
                <b> second</b> room, and the site shows both as "{displayOf(intent.room.key)}" at different prices.
                <span onClick={() => set('roomCategory', intent.room.key)}
                  style={{ display:'inline-block', marginLeft:6, fontWeight:800, color:'#0e8a9c', cursor:'pointer' }}>
                  Use theirs
                </span>
              </div>
            )}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:11 }}>
            <label style={{ display:'block' }}>
              <div style={label}>Staying from</div>
              <input type="date" value={v.beginDate} onChange={e => set('beginDate', e.target.value)} className="a-input" />
            </label>
            <label style={{ display:'block' }}>
              <div style={label}>Until</div>
              <input type="date" value={v.endDate} onChange={e => set('endDate', e.target.value)} className="a-input" />
            </label>
          </div>

          <div>
            <div style={label}>Guests</div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:9 }}>
              <input type="number" min="1" max="10" value={v.adults} onChange={e => set('adults', e.target.value)} className="a-input" style={{ width:74 }} />
              <span style={{ fontSize:13.5, color:'#5a726f', fontWeight:700 }}>adults</span>
              <span onClick={addBand} className="a-ghost" style={{ marginLeft:'auto', padding:'7px 12px', borderRadius:10, fontSize:12.5, cursor:'pointer' }}>+ Children</span>
            </div>

            {v.bands.map((b, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:7, marginBottom:7, padding:'9px 11px', borderRadius:12, background:'rgba(14,138,122,0.06)' }}>
                <input type="number" min="1" max="6" value={b.count} onChange={e => setBand(i, { count: e.target.value })} className="a-input" style={{ width:58, padding:'7px 9px' }} />
                <span style={{ fontSize:12.5, color:'#5a726f', fontWeight:700 }}>aged</span>
                <input type="number" min="0" max="17" value={b.minAge} onChange={e => setBand(i, { minAge: e.target.value })} className="a-input" style={{ width:58, padding:'7px 9px' }} />
                <span style={{ fontSize:12.5, color:'#5a726f' }}>to</span>
                <input type="number" min="0" max="17" value={b.maxAge} onChange={e => setBand(i, { maxAge: e.target.value })} className="a-input" style={{ width:58, padding:'7px 9px' }} />
                <span onClick={() => dropBand(i)} style={{ marginLeft:'auto', cursor:'pointer', color:'#c0492f', fontWeight:800 }}>×</span>
              </div>
            ))}
            <div style={{ fontSize:12, color:'#5a726f' }}>
              The price applies to exactly this party — the supplier's own prices work the same way.
            </div>
          </div>

          <label style={{ display:'block' }}>
            <div style={label}>Price for the room</div>
            <input type="number" min="0" step="1" value={v.price} onChange={e => set('price', e.target.value)} className="a-input" placeholder="275" />
          </label>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:11 }}>
            <label style={{ display:'block' }}>
              <div style={label}>Minimum nights</div>
              <input type="number" min="0" max="365" value={v.minimumStay} onChange={e => set('minimumStay', e.target.value)} className="a-input" />
            </label>
            <label style={{ display:'block' }}>
              <div style={label}>Maximum nights</div>
              <input type="number" min="0" max="365" value={v.maximumStay} onChange={e => set('maximumStay', e.target.value)} className="a-input" placeholder="0 = no limit" />
            </label>
          </div>

          <label style={{ display:'block' }}>
            <div style={label}>Board</div>
            <input list="hc-boards" value={v.mealTypeCode} onChange={e => set('mealTypeCode', e.target.value)} className="a-input" placeholder="All Inclusive" />
            <datalist id="hc-boards">{boards.map(b => <option key={b} value={b} />)}</datalist>
          </label>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:11 }}>
            <label style={{ display:'block' }}>
              <div style={label}>On sale from</div>
              <input type="date" value={v.bookingBegin} onChange={e => set('bookingBegin', e.target.value)} className="a-input" />
            </label>
            <label style={{ display:'block' }}>
              <div style={label}>On sale until</div>
              <input type="date" value={v.bookingEnd} onChange={e => set('bookingEnd', e.target.value)} className="a-input" />
            </label>
          </div>
          <div style={{ fontSize:12, color:'#5a726f', marginTop:-8 }}>
            Optional — an early-booking price stops selling on its own once the window closes.
          </div>

          <label style={{ display:'block' }}>
            <div style={label}>Note to yourself</div>
            <input value={v.note} onChange={e => set('note', e.target.value)} className="a-input" placeholder="Negotiated direct with the hotel" />
          </label>
        </div>

        {error && <div style={{ marginTop:14, fontSize:13, fontWeight:700, color:'#c0492f' }}>{error}</div>}
        {saved && <div style={{ marginTop:14, fontSize:13, fontWeight:700, color:'#1a7a48' }}>{saved}</div>}

        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <span onClick={onClose} className="a-ghost" style={{ flex:1, textAlign:'center', padding:13, borderRadius:13, fontSize:14, cursor:'pointer' }}>Cancel</span>
          <button onClick={() => save(false)} disabled={saving} className="a-primary" style={{ flex:1, padding:13, borderRadius:13, fontSize:14, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : editing ? 'Save' : 'Add price'}
          </button>
        </div>

        {!editing && (
          <div onClick={() => save(true)} style={{ marginTop:11, textAlign:'center', fontSize:13, fontWeight:700, color:'#0e8a9c', cursor:'pointer' }}>
            Save and add another season
          </div>
        )}

        {editing && (
          <div onClick={remove} style={{ marginTop:14, textAlign:'center', fontSize:13, fontWeight:700, color:'#c0492f', cursor:'pointer' }}>
            Delete this price
          </div>
        )}
      </div>
    </div>
  );
}
